import React, {
  useRef,
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle,
} from "react";
import SearchPanel from "@widgets/SearchPanel";
import { fnGetDataSet, fnSave, fnGetTableById } from "@shared/api/restApi";
import { getInitFormData, setConfigToColumns } from "@shared/utils/CommonUtils";
import { showDialog } from "@widgets/CommonDialog";
import { global, globalMenu } from "@shared/libs/Global";
import SnGrid from "@shared/components/GridV2/SnGrid";
import {
  CellValueChangedEvent,
  ColumnConfig,
  RowData,
  SnGridRef,
} from "@shared/components/GridV2/SnGridTypes";
import { menuInfo } from "@shared/type/CommonType";
import { showPopupModal } from "@widgets/CommonModal";
import SN_PRD_BAS_REG_004_POP_001 from "./SN_PRD_BAS_REG_004_POP_001";
import { openAddInfoPopup } from "@widgets/commonpopup/sys/openAddInfoPopup";
import { openAddInfoRegPopup } from "@widgets/commonpopup/sys/openAddInfoRegPopup";
import {
  buildJsAddinfoFromFlags,
  extractAddInfoFlags,
  hydrateRowsWithAddInfo,
} from "@shared/utils/AddInfoUtils";
interface Props {
  Menuprops: menuInfo;
}

type BomHeaderState = "I" | "U" | "D";
type CdOpOption = { code: string; name: string; [key: string]: unknown };

const SN_PRD_BAS_REG_004 = forwardRef(({ Menuprops }: Props, ref) => {
  const { userInfo } = global();
  const searchRef = useRef<any>(null);
  const gridRefs = useRef<{ [key: string]: React.RefObject<SnGridRef> }>({
    grid1: React.createRef<SnGridRef>(),
    sheet1: React.createRef<SnGridRef>(),
  });
  const copyRow = useRef<RowData | null>(null);
  const isPaste = useRef(false);
  const isBulkUpdating = useRef(false);

  const today = new Date();
  const todayform = `${today.getFullYear()}${(today.getMonth() + 1)
    .toString()
    .padStart(2, "0")}${today.getDate().toString().padStart(2, "0")}`;
  const [addInfoFlags, setAddInfoFlags] = useState<string[]>([]);
  const [formData, setFormData] = useState<RowData>();
  const [formControlData, setFormControlData] = useState<any[]>([]);
  const [columns, setColumns] = useState<{
    grid1: ColumnConfig[];
    sheet1: ColumnConfig[];
  }>({
    grid1: [],
    sheet1: [],
  });
  const [dataSources, setDataSources] = useState<{
    bomh: RowData[];
    grid1: RowData[];
    sheet1: RowData[];
  }>({
    bomh: [],
    grid1: [],
    sheet1: [],
  });
  const [headerInfo, setHeaderInfo] = useState<any>({
    grid1: {},
  });
  const [cdOpList, setCdOpList] = useState<CdOpOption[]>([]);
  const [gridFocus, setGridFocus] = useState(false);
  const loadAddInfoFlags = async (
    idTarget = "grid2",
    setFlags = setAddInfoFlags
  ) => {
    const result: any = await fnGetDataSet({
      id: "sys_addinfo_pop_01_01",
      params: {
        cd_firm: userInfo?.cd_firm ?? "",
        cd_menu: Menuprops?.cd_menu ?? "",
        id_target: idTarget,
      },
    });
    const rows = result?.[0] || [];
    const flags = extractAddInfoFlags(rows);
    setFlags(flags);
    return flags;
  };
  const getGridCurrent = (key: string): SnGridRef => {
    const grid = gridRefs.current[key]?.current;
    if (!grid) {
      throw new Error(`Grid '${key}' is not mounted yet`);
    }
    return grid;
  };

  const getErrorMessage = (error: unknown, fallback: string) => {
    if (error instanceof Error) return error.message;
    if (typeof error === "string") return error;
    return fallback;
  };

  const updateCdOpList = (nextCdOpList: CdOpOption[]) => {
    setCdOpList(nextCdOpList);
    setColumns((prev) => ({
      ...prev,
      grid1: prev.grid1.map((column) =>
        column.cd_column === "cd_op"
          ? { ...column, js_combo: JSON.stringify(nextCdOpList) }
          : column
      ),
    }));
  };

  useEffect(() => {
    const fetchform = async () => {
      try {
        const rtn = await getInitFormData();
        setFormControlData(rtn.formControlData);
        setFormData(rtn.formData);
        setColumns({
          grid1: rtn.grid.grid2,
          sheet1: rtn.grid.sheet1,
        });
      } catch (error: unknown) {
        await showDialog(
          "error",
          getErrorMessage(error, "초기 화면 정보를 불러오지 못했습니다.")
        );
      }
    };
    fetchform();
  }, []);

  useEffect(() => {
    if (!gridFocus) return;
    gridRefs.current.grid1.current?.selectAndFocusRow(0);
    setGridFocus(false);
  }, [gridFocus]);

  useImperativeHandle(ref, () => ({
    onSearch,
    onDelete,
    onAddRow,
    onSave,
    onExcelUpload,
    onExcelDownLoad,
  }));

  const onSearch = async () => {
    const grid = getGridCurrent("grid1");
    const modifiedRows = grid.getChange();
    if (modifiedRows.modifiedRowcount > 0) {
      const confirm = await showDialog(
        "confirm",
        "수정된 내용이 있습니다. 저장하시겠습니까?"
      );
      if (confirm) {
        const saved = await onSave();
        if (!saved) return false;
      }
    }

    try {
      const param = {
        id: "sn_prd_bas_reg_004_01",
        params: searchRef.current.getFormdata(),
      };

      setDataSources((prev) => ({ ...prev, bomh: [], grid1: [] }));
      const data = await fnGetDataSet(param);
      updateCdOpList(data[2] ?? []);
      const flags =
        addInfoFlags.length > 0
          ? addInfoFlags
          : await loadAddInfoFlags("grid2", setAddInfoFlags);
      setDataSources((prev) => ({
        ...prev,
        bomh: data[0] ?? [],
        grid1: hydrateRowsWithAddInfo(data[1] || [], flags),
      }));

      if (data[1]?.length > 0) {
        setGridFocus(true);
      }

      grid.resetUpdate();
      copyRow.current = null;
      return true;
    } catch (error: unknown) {
      await showDialog(
        "error",
        getErrorMessage(error, "조회 중 오류가 발생했습니다.")
      );
      return false;
    }
  };

  const onExcelUpload = async () => {
    try {
      const component = (
        <SN_PRD_BAS_REG_004_POP_001
          Menuprops={Menuprops}
          PkFields={["cd_item, no_line"]}
          tableName="prd_boml"
          controllerName="SN_PRD_BAS_REG_004_SAVE_01"
          cdOpList={cdOpList}
          cd_biz={searchRef.current.getFormdata().cd_biz}
          userInfo={userInfo}
        />
      );

      await showPopupModal({
        title: "엑셀 업로드",
        children: component,
        width: "80%",
        height: "90VH",
        okayBtnText: "저장",
      });
      return true;
    } catch (error: unknown) {
      await showDialog("error", getErrorMessage(error, "Excel 업로드 오류"));
      return false;
    }
  };

  const onExcelDownLoad = async () => {
    try {
      if (searchRef.current.getFormdata().cd_item.trim() === "") {
        const confirm = await showDialog(
          "confirm",
          "전체 내역을 다운받으시겠습니까?"
        );
        if (!confirm) return false;
      }
      const param = {
        id: "sn_prd_bas_reg_004_02",
        params: searchRef.current.getFormdata(),
      };

      setDataSources((prev) => ({ ...prev, sheet1: [] }));
      const data = await fnGetDataSet(param);
      setDataSources((prev) => ({ ...prev, sheet1: data[0] ?? [] }));

      await new Promise((resolve) => setTimeout(resolve, 500));
      const sheetGrid = getGridCurrent("sheet1");
      const displayedColumns = sheetGrid.getAllDisplayedColumns() || [];
      const columnKeys = displayedColumns.map((column) => column.getColId());

      sheetGrid.exportDataAsExcel({
        columnKeys,
        skipPinnedBottom: true,
        fileName: `${globalMenu.getState().activeMenuInfo.nm_menu}.xlsx`,
        sheetName: "Sheet1",
      });
      return true;
    } catch (error: unknown) {
      await showDialog("error", getErrorMessage(error, "Excel 다운로드 오류"));
      return false;
    }
  };

  const onAddRow = async () => {
    try {
      if (searchRef.current.getFormdata().cd_item.trim() === "") {
        await showDialog("alter", "품목을 선택하시기 바랍니다");
        return false;
      }

      const payload = {
        id: "item_rev",
        params: {
          cd_firm: userInfo.cd_firm,
          cd_item: searchRef.current.getFormdata().cd_item,
        },
      };

      const data = await fnGetTableById(payload);
      if (Array.isArray(data) && data.length > 0) {
        await showDialog("alter", "개정관리되는 품목입니다.");
        return false;
      }

      getGridCurrent("grid1").addRow();
      return true;
    } catch (error: unknown) {
      await showDialog(
        "error",
        getErrorMessage(error, "행 추가 중 오류가 발생했습니다.")
      );
      return false;
    }
  };

  const onCellValueChanged = async (event: CellValueChangedEvent) => {
    try {
      if (!event || isBulkUpdating.current) return;

      if (event.colId === "rt_loss" || event.colId === "qt_just") {
        calculateAmounts(event.rowNode, event.rowData);
      }

      if (event.colId === "cd_matl") {
        await applyParentItemFields();
      }

      if (event.colId === "dt_start" || event.colId === "dt_end") {
        const start = Number(event.rowData?.dt_start);
        const end = Number(event.rowData?.dt_end);

        if (start > end) {
          await showDialog(
            "alter",
            "시작일 보다 종료일이 더 빠릅니다.\r\n날짜를 확인해주세요."
          );
          getGridCurrent("grid1").stopEditing();
          event.rowNode.setDataValue(event.colId, event.oldValue, "noEvent");
          return;
        }
      }

      if (event.colId === "cd_op") {
        await applyCdOpToAllRows(event);
      }
    } catch (error: unknown) {
      await showDialog(
        "error",
        getErrorMessage(error, "그리드 변경 처리 중 오류가 발생했습니다.")
      );
    }
  };

  const applyParentItemFields = async () => {
    const grid = getGridCurrent("grid1");
    const parentItem = searchRef.current.getFormdata();
    const rowNodes: CellValueChangedEvent["rowNode"][] = [];

    grid.forEachNode((node) => {
      node.setDataValue("cd_item", parentItem.cd_item, "noEvent");
      node.setDataValue("nm_item", parentItem.nm_item, "noEvent");
      node.setDataValue("dc_item_spec", parentItem.dc_item_spec, "noEvent");
      node.setDataValue("dc_item_unit", parentItem.dc_item_unit, "noEvent");
      rowNodes.push(node);
    });

    const invalidNode = rowNodes.find(
      (node) => node.data?.cd_item === node.data?.cd_matl
    );

    if (invalidNode) {
      await showDialog(
        "alter",
        "모품목과 동일한 품목을 자품목으로 등록할 수 없습니다."
      );
      invalidNode.setDataValue("cd_matl", "", "noEvent");
      invalidNode.setDataValue("nm_matl", "", "noEvent");
      invalidNode.setDataValue("dc_item_spec_matl", "", "noEvent");
      invalidNode.setDataValue("dc_item_unit_matl", "", "noEvent");
      invalidNode.setSelected(true);
      grid.setFocusedCell(invalidNode.rowIndex ?? 0, "cd_biz");
    }
  };

  const applyCdOpToAllRows = async (event: CellValueChangedEvent) => {
    if (event.rowData?.yn_condition === "Y") {
      event.rowNode.setDataValue("yn_condition", "N", "noEvent");
      return;
    }

    const ok = await showDialog(
      "confirm",
      "해당 공정으로 일괄 적용 하시겠습니까?"
    );
    if (!ok) return;

    const grid = getGridCurrent("grid1");
    isBulkUpdating.current = true;
    try {
      grid.forEachNode((node) => {
        node.setDataValue("yn_condition", "Y", "noEvent");
        node.setDataValue("cd_op", event.newValue, "noEvent");
        node.setDataValue("yn_condition", "N", "noEvent");
      });
    } finally {
      isBulkUpdating.current = false;
    }
  };

  const handleContextMenu = async (params: { id?: string }) => {
    const grid = getGridCurrent("grid1");
    const selectedRow =
      grid.getSelectedRows()[0] ?? grid.focusedRowNode()?.data;
    if (!selectedRow) return;

    switch (params.id) {
      case "wf_sn_prd_bas_info":
        (async () => {
          const rowNode = grid.focusedRowNode();
          if (!rowNode) {
            await showDialog("alter", "선택된 행이 없습니다.");
            return;
          }
          if (!Menuprops.cd_menu) {
            await showDialog("alter", "메뉴 코드가 없습니다.");
            return;
          }

          // id_target은 호출 메뉴에서 전달 (고정값 아님)
          const idTarget = "grid2";

          const result = await openAddInfoRegPopup({
            title: "BOM부가항목",
            cd_menu: Menuprops.cd_menu,
            id_target: idTarget,
            rowData: { ...rowNode.data },
            width: "980px",
            height: "760px",
            rememberSizeKey: "SN_PRD_BAS_REG_004",
          });
          if (!result || result === "close") return;

          const values = result?.values ?? {};
          Object.keys(values).forEach((key) => {
            rowNode.setDataValue(key, values[key]);
          });
        })();
        break;
      case "fn_rowcopy_ms":
        copyRow.current = { ...selectedRow };
        break;
      case "fn_rowpaste_ms":
        if (!copyRow.current) {
          await showDialog("alter", "품목을 선택하시기 바랍니다.");
          return;
        }
        isPaste.current = true;
        grid.addRow();
        isPaste.current = false;
        break;
    }
  };

  const calculateAmounts = (
    rowNode: CellValueChangedEvent["rowNode"],
    row: RowData
  ) => {
    const qtJust = Number(row.qt_just ?? 0);
    const rtLoss = Number(row.rt_loss ?? 0);
    const qtNeed = qtJust + qtJust * rtLoss * 0.01;
    rowNode.setDataValue("qt_need", qtNeed, "noEvent");
  };

  const onBeforeAddRow = () => {
    try {
      const selectedFormData = searchRef.current.getFormdata();
      const allRowData: RowData[] = [];
      getGridCurrent("grid1").forEachNode((node) => {
        allRowData.push(node.data);
      });

      const maxNoLine = allRowData
        .filter((row) => row.cd_biz === selectedFormData.cd_biz)
        .map((row) => Number(row.no_line || 0))
        .reduce((max, current) => Math.max(max, current), 0);

      if (isPaste.current && copyRow.current) {
        const pastedRow = { ...copyRow.current };
        delete pastedRow.KeyField;
        delete pastedRow.state;
        delete pastedRow.block;
        return {
          ...pastedRow,
          no_line: maxNoLine + 1,
        };
      }

      return {
        cd_firm: userInfo.cd_firm,
        cd_biz: selectedFormData.cd_biz,
        cd_item: selectedFormData.cd_item,
        nm_item: selectedFormData.nm_item,
        dc_item_spec: selectedFormData.dc_item_spec,
        dc_item_unit: selectedFormData.dc_item_unit,
        no_line: maxNoLine + 1,
        tp_bom: "STD",
        qt_just: 1,
        qt_avg: 0,
        rt_loss: 0,
        qt_need: 1,
        qt_standard: 0,
        dt_start: todayform,
        dt_end: "21001231",
        yn_condition: "N",
        cd_user_reg: userInfo.cd_user,
      };
    } catch (error: unknown) {
      showDialog(
        "error",
        getErrorMessage(error, "행 기본값 생성 중 오류가 발생했습니다.")
      );
      return false;
    }
  };

  const onDelete = async () => {
    const grid = getGridCurrent("grid1");
    if (grid.getDisplayedRowCount() < 1) return false;

    const confirm = await showDialog(
      "confirm",
      "BOM 전체 내역이 삭제됩니다. 삭제하시겠습니까?"
    );
    if (!confirm) return false;

    grid.deleteAllRows();
    return await onSave();
  };

  const onDeleteRow = async () => {
    try {
      getGridCurrent("grid1").deleteSelectedRows();
      return true;
    } catch (error: unknown) {
      await showDialog(
        "error",
        getErrorMessage(error, "행 삭제 중 오류가 발생했습니다.")
      );
      return false;
    }
  };

  const onSave = async () => {
    try {
      const grid = getGridCurrent("grid1");
      grid.stopEditing();
      const requiredValuesCheckH = grid.requiredValuesCheck();
      if (!requiredValuesCheckH) {
        const changeData = grid.getChange();
        const cdItem =
          changeData.insert?.[0]?.cd_item ??
          changeData.update?.[0]?.cd_item ??
          changeData.delete?.[0]?.cd_item ??
          "";

        if (!cdItem) return false;

        const searchData = searchRef.current.getFormdata();
        const param = {
          id: "popup_item_02",
          params: {
            cd_firm: userInfo.cd_firm,
            cd_biz: searchData.cd_biz ? searchData.cd_biz : userInfo.cd_biz,
            cd_item: cdItem,
          },
        };

        const data = await fnGetDataSet(param);
        if (data?.[0]?.[0]) {
          const item = data[0][0];
          setFormData({
            ...searchData,
            cd_item: item.cd_item,
            nm_item: item.nm_item,
          });
        }

        return false;
      }

      const allRows: RowData[] = [];
      grid.forEachNode((node) => {
        allRows.push(node.data);
      });
      const firstCdBiz = allRows[0]?.cd_biz;
      const hasMismatch = allRows.some((row) => row?.cd_biz !== firstCdBiz);

      if (hasMismatch) {
        await showDialog("alter", "사업장이 서로 다릅니다.");
        return false;
      }

      const changesRow = grid.getChange();
      if (changesRow.modifiedRowcount === 0) {
        await showDialog("alter", "변경된 내용이 없습니다.");
        return false;
      }

      const insertRows = changesRow.insert || [];
      const deleteRows = changesRow.delete || [];
      let bomhSaveData: {
        insert?: RowData[];
        update?: RowData[];
        delete?: RowData[];
      };

      if (deleteRows.length > 0 && grid.getDisplayedRowCount() === 0) {
        bomhSaveData = { delete: getBomHData("D", deleteRows[0]?.cd_item) };
        setDataSources((prev) => ({ ...prev, bomh: [] }));
      } else if (insertRows.length > 0 && dataSources.bomh.length < 1) {
        const insertHeader = getBomHData("I", insertRows[0]?.cd_item);
        bomhSaveData = { insert: insertHeader };
        setDataSources((prev) => ({ ...prev, bomh: insertHeader }));
      } else {
        bomhSaveData = {
          update: getBomHData("U", searchRef.current.getFormdata().cd_item),
        };
      }
      // 한글 주석: 저장 시점에 flags 상태가 비어있으면 재조회하여 누락 저장을 방지한다.
      const flagsForSave =
        addInfoFlags.length > 0
          ? addInfoFlags
          : await loadAddInfoFlags("grid2", setAddInfoFlags);

      // 한글 주석: 저장 직전에 헤더 변경행의 동적 부가항목 컬럼값을 js_addinfo로 묶는다.
      const bomWithAddInfo = {
        ...changesRow,
        insert: (changesRow.insert ?? []).map((row: any) => ({
          ...row,
          js_addinfo: buildJsAddinfoFromFlags(row, flagsForSave),
        })),
        update: (changesRow.update ?? []).map((row: any) => ({
          ...row,
          js_addinfo: buildJsAddinfoFromFlags(row, flagsForSave),
        })),
      };
      const saveData = {
        prd_bomh: bomhSaveData,
        prd_boml: bomWithAddInfo,
      };
      const ok = await fnSave("SN_PRD_BAS_REG_004_SAVE_01", saveData);
      if (!ok) return false;

      grid.resetUpdate();
      return true;
    } catch (error: unknown) {
      await showDialog(
        "error",
        getErrorMessage(error, "저장 중 오류가 발생했습니다.")
      );
      return false;
    }
  };

  const getBomHData = (state: BomHeaderState, cdItem: string) => {
    const base = {
      cd_firm: userInfo.cd_firm,
      cd_biz: userInfo.cd_biz,
      cd_item: cdItem,
      cd_emp: userInfo.cd_emp,
      dt_reg: todayform,
    };

    if (state === "I" || state === "U") {
      return [
        {
          ...base,
          dc_rmk: searchRef.current.getFormdata().dc_rmk,
        },
      ];
    }

    return [base];
  };
  const onGrid1ExtraHeaderMenuClick = async (event: {
    id: string;
    params: any;
  }) => {
    switch (event.id) {
      case "extra_item_setting":
        // 부가항목설정 실행
        await onOpenAddInfoPopup("grid2");
        break;
      default:
        break;
    }
  };
  const onOpenAddInfoPopup = async (idTarget = "grid2") => {
    // id_target에 해당하는 현재 컬럼 메타를 설정 팝업으로 전달한다.
    const columnInfos = columns.grid1;
    const saved = await openAddInfoPopup({
      cd_menu: Menuprops.cd_menu!,
      id_target: idTarget,
      columnInfos,
    });

    if (saved) {
      const rtn: any = await getInitFormData();
      setFormControlData(rtn.formControlData);
      setFormData((prev: any) => ({
        ...(rtn.formData || {}),
        ...(prev || {}),
      }));

      const grid1 = rtn.grid.grid2;
      const configGrid1 = setConfigToColumns(grid1, "SAL0010");
      setColumns({
        grid1: configGrid1,
        sheet1: rtn.grid.sheet1,
      });
      setHeaderInfo({
        grid1: rtn.header.grid1,
      });

      // 한글 주석: 항목설정 변경 후 저장 키 목록을 다시 로드한다.
      await loadAddInfoFlags(idTarget, setAddInfoFlags);

      await onSearch();
    }
  };
  const grids = {
    grid1: {
      columns: columns.grid1,
      dataSource: dataSources.grid1,
      etcProps: {
        rowSelection: "single" as const,
        title: "BOM정보",
        extraHeaderMenuItems: [
          {
            id: "extra_item_setting",
            name: "부가항목설정",
            visible: true,
          },
        ],
      },
      events: {
        onBtnAddClick: onAddRow,
        onBtnDeleteClick: onDeleteRow,
        onCellValueChanged,
        onBeforeAddRow,
        onContextMenu: handleContextMenu,
        onExtraHeaderMenuClick: onGrid1ExtraHeaderMenuClick,
      },
    },
    sheet1: {
      columns: columns.sheet1,
      dataSource: dataSources.sheet1,
    },
  };

  return (
    <div className="h-full flex flex-col">
      <div className="mb-1">
        <SearchPanel
          ref={searchRef}
          data={formData}
          controls={formControlData}
        ></SearchPanel>
      </div>
      <div className="flex-1 h-full min-h-0 space-y-1.5">
        <SnGrid
          ref={gridRefs.current.grid1}
          columnConfig={grids.grid1.columns}
          rowData={grids.grid1.dataSource}
          etcProps={grids.grid1.etcProps}
          events={grids.grid1.events}
        />
      </div>
      <div style={{ display: "none" }}>
        <SnGrid
          ref={gridRefs.current.sheet1}
          columnConfig={grids.sheet1.columns}
          rowData={grids.sheet1.dataSource}
          etcProps={{ isEditable: false }}
        />
      </div>
    </div>
  );
});

export default SN_PRD_BAS_REG_004;
